import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, Observable } from 'rxjs';
import { Organization } from 'app/models/organization.model';
import { OrganizationsState } from 'app/state-management/states/organizations.states';
import {
  ComboGroup,
  DisplayedProduct,
  DisplayedGroup,
  Group,
  MenuSection,
  MenuSyncOfSale,
  ModifierGroup,
  PositionTypeEnum,
  Product,
  Size,
  Dictionary,
  MenuEventsQueryParams,
  GecoPosMenu, OverridePositionsMap, MenuBackup, CreateMenuBackup, GetMenuRequest,
} from 'app/models/menu.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {
  EditMenuModalComponent,
  EditMenuModalComponentData
} from '../../shared/edit-menu-modal/edit-menu-modal.component';
import {
  EditProductModalComponent,
  EditProductsModalComponentDataType
} from 'app/pages/menu/shared/edit-product-modal/edit-product-modal.component';
import {
  EditGroupDialogData,
  EditGroupModalComponent
} from 'app/pages/menu/shared/edit-group-modal/edit-group-modal.component';
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import {
  SelectProductsModalComponent,
  SelectProductsModalComponentDataType
} from 'app/pages/menu/shared/select-products-modal/select-products-modal.component';
import { MenuOverrideService } from 'app/services/menu-override.service';
import {
  SelectMenuGroupsModalComponent,
  SelectMenuGroupsModalComponentDataType
} from 'app/pages/menu/shared/select-menu-groups-modal/select-menu-groups-modal.component';
import { Currency } from 'app/models/currency.model';
import { SnackbarService } from 'app/services/snackbar.service';
import { RoleMembership, selectRole } from 'app/reducers/context.selectors';
import { Store } from '@ngrx/store';
import { Select, Store as ngxsStore } from '@ngxs/store';
import * as MenuSelectors from 'app/reducers/menu.selectors';
import { MenuService } from 'app/services/menu.service';
import { PipeConfiguration } from 'app/models/pipe.model';
import { GECOPOS_ID, GECOPOSSALES_ID } from 'app/models/externalSystem.model';
import { PipeConfigurationDetailed } from 'app/models/organizations.models';
import { LoadRevisionForPipeGroups } from 'app/state-management/actions/organizations.actions';
import { utcToLocalFormat } from 'app/shared/pipes/utc-to-local.pipe';
import { APIService } from 'app/services/api.service';
import { menuHelper } from 'app/helpers/menuHelper';
import * as MenuActions from 'app/reducers/menu.actions';
import { MenuConverter } from 'app/services/menu.converter';
import { DishesService } from 'app/services/dishes.service';
import { setMenusPending } from 'app/reducers/menu.actions';
import {MenuBackupService} from "app/services/menu-backup.service";
import {AppHelper} from "app/helpers/appHelper";
import {
  EditMenuBackupComponent,
  EditMenuBackupModalDataType
} from "app/pages/menu/shared/edit-menu-backup/edit-menu-backup.component";
import {
  MenuPipesModalComponent,
  MenuPipesModalComponentDataType
} from '../../shared/menu-pipes-modal/menu-pipes-modal.component';
import {
  MigrateRelatedProductsDialogComponent,
  MigrateRelatedProductsDialogData
} from '../../shared/migrate-related-products-dialog/migrate-related-products-dialog.component';
import { environment } from 'environments/environment';
type PublishedFor = 'staging' | 'kiosk' | 'production';

@UntilDestroy()
@Component({
  selector: 'app-menu-main-content',
  templateUrl: './menu-main-content.component.html',
  styleUrls: ['./menu-main-content.component.scss']
})
export class MenuMainContentComponent implements OnInit {
  dataIsLoaded: boolean = false;
  showDeletedMenuSections: boolean = false;
  groups: DisplayedGroup[] = [];
  hierarchicalGroups: DisplayedGroup[] = [];
  sortedOriginalGroups: Group[] = [];
  selectedGroup: DisplayedGroup;
  overridePositionsMap: OverridePositionsMap;
  currentMenuSection: MenuSection;
  currentOrganization: Organization;
  searchQuery: string;
  currency: Currency | null;

  isShowGroupHierarchy: boolean;
  gecoPosMenu: GecoPosMenu;

  allowOverrides: boolean;

  cachedBackups: Map<string, GecoPosMenu> = new Map<string, GecoPosMenu>();
  menuBackups: MenuBackup[];
  menuSections: MenuSection[];
  groupsMap: Map<string, DisplayedGroup>;
  originalGroupsMap: Map<string, Group>;
  products: Map<string, DisplayedProduct>;
  originalProducts: Map<string, Product>;
  sizes: Map<string, Size>;
  originalSizes: Map<string, Size>;
  modifierGroups: Map<string, ModifierGroup>;
  originalModifierGroups: Map<string, ModifierGroup>;
  comboGroups: Map<string, ComboGroup>;
  originalComboGroups: Map<string, ComboGroup>;
  menuSyncsOfSale: MenuSyncOfSale[] = [];

  menuPipeId: string | null = null;
  menuPipe: PipeConfigurationDetailed | null = null;
  dateFormat: string = utcToLocalFormat;
  menuPipes: PipeConfiguration[];

  uploadingMenu: boolean;

  showPipesButton: boolean;

  // Флаг единообразен с `canShowKioskSettings` в internal.component.ts — показываем киоск-
  // специфичное сообщение в UI везде, кроме production-сборок.
  readonly canShowKioskWarning: boolean =
    (environment.publishedFor as PublishedFor) !== 'production';

  // id групп, у которых есть дочерние (parentId у других групп ссылается сюда).
  // Пересоздаётся в subscribeToSelectMenuMainContentComponent при каждом обновлении groups.
  private parentIdsWithChildren: Set<string> = new Set<string>();

  public roleMembership: RoleMembership = {
    isSupport: false,
    isManager: false,
    isAdministrator: false,
    isUser: false,
    isSuperManager: false,
    isVersionsEditor: false,
  };

  @Select(OrganizationsState.getSelectedOrganization) selectedOrganization$: Observable<Organization>;
  @Select(OrganizationsState.getPipes) getPipes$: Observable<PipeConfiguration[]>;
  @Select(OrganizationsState.getPipeDetails) getPipeDetails$: Observable<PipeConfigurationDetailed[]>;

  constructor(
    private activatedRoute: ActivatedRoute,
    public dialog: MatDialog,
    private translate: TranslateService,
    private router: Router,
    private store: Store,
    private ngxsStore: ngxsStore,
    private snackbar: SnackbarService,
    private menuService: MenuService,
    private apiService: APIService,
    private menuConverter: MenuConverter,
    private dishesService: DishesService,
    private menuBackupService: MenuBackupService,
  ) {
    this.showPipesButton = (environment.publishedFor as PublishedFor) === 'production';
  }

  async ngOnInit(): Promise<void> {
    this.subscribeToSelectMenuMainContentComponent();

    const [{menuSectionId}, selectedOrganization, menuSections, pipes, roleMembership, gecoPosMenu, overridePositionsMap] =
      await Promise.all([
        firstValueFrom(this.activatedRoute.params),
        firstValueFrom(this.selectedOrganization$),
        firstValueFrom(this.store.select(MenuSelectors.selectMenuSections)),
        firstValueFrom(this.getPipes$),
        firstValueFrom(this.store.select(selectRole)),
        firstValueFrom(this.store.select(MenuSelectors.selectMenu)),
        firstValueFrom(this.store.select(MenuSelectors.selectOverridePositionsMap)),
      ]);
    if (!menuSections?.length){
      return; // значит эта организация не настроена для работы с gecoPos
    }
    this.menuBackups = await this.menuBackupService.getMenuBackups(selectedOrganization.id);
    this.gecoPosMenu = gecoPosMenu;
    this.currentOrganization = selectedOrganization;
    this.roleMembership = roleMembership;
    this.menuSections = menuSections;

    this.currentMenuSection = menuSections.find(m => m.id === menuSectionId);
    if (this.currentMenuSection.isDeleted){
      this.showDeletedMenuSections = true;
    }
    this.menuPipes = await this.getMenuPipes(this.currentOrganization.id, this.currentMenuSection);
    this.store.select(MenuSelectors.selectMenuBackups)
      .pipe(untilDestroyed(this))
      .subscribe(result => {this.cachedBackups = result;});

    this.store.select(MenuSelectors.selectAllowOverrides)
      .pipe(untilDestroyed(this))
      .subscribe(result => {this.allowOverrides = result;});
    const menuState = this.menuConverter.convertMenu(gecoPosMenu, overridePositionsMap, this.cachedBackups);
    this.store.dispatch(MenuActions.updateMenuState({...menuState, getMenusPending: false, allowOverrides: true}));

    this.dataIsLoaded = true;

    // Требует рефакторинга, если есть необходимость выполнить один раз для всех секций меню,
    // то нужно будет это сделать по-другому
    this.loadRevisionForPipeGroups(pipes);
  }

  subscribeToSelectMenuMainContentComponent(){
    this.store.select(MenuSelectors.selectForMenuMainContentComponent)
      .pipe(untilDestroyed(this))
      .subscribe(({
                    groupMap,
                    originalGroupMap,
                    products,
                    originalProducts,
                    modifierGroups,
                    originalModifierGroups,
                    comboGroups,
                    originalComboGroups,
                    sizes,
                    originalSizes,
                    currency,
                    searchQuery,
                    overridePositionsMap,
                    menuSections,
                  }) => {
        this.groupsMap = groupMap;
        this.originalGroupsMap = originalGroupMap;
        const sortedGroups = menuHelper.sortGroupsByOrderAndId(groupMap.values());
        this.groups = sortedGroups;
        this.hierarchicalGroups = this.getStructuredGroups([...sortedGroups]);
        this.parentIdsWithChildren = this.buildParentIdsWithChildren(sortedGroups);
        this.sortedOriginalGroups = menuHelper.sortGroupsById(originalGroupMap.values());

        this.products = products;
        this.originalProducts = originalProducts;
        this.modifierGroups = modifierGroups;
        this.originalModifierGroups = originalModifierGroups;
        this.comboGroups = comboGroups;
        this.originalComboGroups = originalComboGroups;

        this.sizes = sizes;
        this.originalSizes = originalSizes;

        this.currency = currency;

        this.searchQuery = searchQuery

        if (this.selectedGroup){
          this.selectedGroup = this.groups.find(g => g.externalId === this.selectedGroup.externalId);
        }
        else{
          this.selectedGroup = this.groups[0];
        }

        this.overridePositionsMap = overridePositionsMap;

        this.selectGroup(this.selectedGroup);

        this.menuSections = menuSections;
      });
  }

  selectProductsToGroup() {
    const productIds = this.selectedGroup.groupStructure.productsInGroup.map(p => p.externalProductId);

    this.dialog.open(SelectProductsModalComponent, {
      data: {
        title: this.translate.instant('MENU.SECTIONS.SELECT_DISHES', {name: this.selectedGroup.name}),
        productIds: productIds,
      } as SelectProductsModalComponentDataType
    }).afterClosed().subscribe(async res => {
      if (res) {
        const newProductIds = res.data;
        await this.saveProductIds(productIds, newProductIds);
      }
    });
  }

  //oldProductIds: уже упорядоченный
  //newProductIds: уже упорядоченный по оригиналу
  async saveProductIds(oldProductIds: string[], newProductIds: string[]): Promise<void> {
    if ([...oldProductIds].sort().toString() == [...newProductIds].sort().toString()){
      return;
    }

    let ids: string[] = [];
    let startIds: string[] = [];
    let endIds: string[] = [];

    let originalGroupIds = this.originalGroupsMap.get(this.selectedGroup.externalId)
      .groupStructure.productsInGroup.map(p => p.externalProductId);

    // если сортировка оригинальных продуктов не изменена,то вновь добавленный родной продукт должен встать на свое место, а не в конец
    if(!this.selectedGroup.hasReOrderingProductInGroup){
      startIds = [...originalGroupIds].filter(oId => newProductIds.some(newId => newId == oId));
      endIds = [...newProductIds].filter(newId => !startIds.some(id => newId == id));
    }
    else{ // добавляем в конец вновь включенный продукт в группу
      for (let newProductId of newProductIds) {
        if (oldProductIds.some(id => id == newProductId)){
          startIds.push(newProductId);
        }
        else{
          endIds.push(newProductId);
        }
      }
    }

    ids = [...startIds, ...endIds];

    const overrideGroupStructure = MenuOverrideService.createOrUpdateProductIdsInGroupStructure(
      ids,
      this.selectedGroup.externalId,
      this.currentMenuSection.id,
      this.overridePositionsMap);

    await this.menuService.saveOverridePositions(this.currentMenuSection.id,[overrideGroupStructure], [this.currentMenuSection.id]);
  }

  async selectGroupsToMenu() {
    const currentMenuSection = this.menuSections.find(menuSection => menuSection.id == this.currentMenuSection.id);

    let groupIds = this.groups.map(g => g.externalId);

    const result = await firstValueFrom(
      this.dialog.open(SelectMenuGroupsModalComponent, {
        data: {
          menuName: currentMenuSection.name,
          groupIds: groupIds,
        } as SelectMenuGroupsModalComponentDataType
      }).afterClosed()
    );

    if (result) {
      const newGroupIds = result.data;
      await this.saveGroupIds(groupIds, newGroupIds);
    }
  }

  //oldGroupIds: уже упорядоченный
  //newGroupIds: уже упорядоченный по оригиналу
  async saveGroupIds(oldGroupIds: string[], newGroupIds: string[]) {
    if ([...oldGroupIds].sort().toString() === [...newGroupIds].sort().toString())
    {
      return;
    }

    let ids: string[] = [];

    if (!this.hasReOrderingGroups()){
      ids = newGroupIds;
    }
    else{
      ids = [...oldGroupIds].filter(oldId => newGroupIds.some(newId => newId === oldId));
      let endIds: string[] = [...newGroupIds].filter(newId => !oldGroupIds.some(oldId => oldId == newId));
      if (endIds.length){
        ids = [...ids, ...endIds];
      }
    }

    const overrideGroupStructure = MenuOverrideService.saveGroupIdsInMenu(
      ids,
      this.currentMenuSection.menuId,
      this.currentMenuSection.id,
      this.overridePositionsMap);
    await this.menuService.saveOverridePositions(this.currentMenuSection.id,[overrideGroupStructure], [this.currentMenuSection.id]);
  }

  getMenuSectionsIncludeChildren(): MenuSection[] {
    return [...this.menuSections.filter(ms => ms.id === this.currentMenuSection.id), // current menu section
      ...this.menuSections.filter(ms => ms.parentMenuSectionId === this.currentMenuSection.id && !ms.isDeleted)]; /// all child menu sections.
  }

  onOpenEditMenuGroupDialog(group: Group): void {
    if(!this.allowOverrides){
      return;
    }
    const originalGroup = this.originalGroupsMap.get(group.externalId);
    const groups = this.groupsMap;

    this.dialog.open(EditGroupModalComponent, {
      data: {
        group,
        groupsMap: groups,
        originalGroup,
        overrideGroupPosition: this.overridePositionsMap.getOverride(PositionTypeEnum.Group, group.externalId),
        overrideGroupStructurePosition: this.overridePositionsMap.getOverride(PositionTypeEnum.GroupStructure, group.externalId),
        menuSections: this.getMenuSectionsIncludeChildren(),
        menuSectionId: this.currentMenuSection.id,
        organization: this.currentOrganization,
        organizationId: this.currentOrganization.id,
        allowOverrides: this.allowOverrides,
        products: this.products,
      } as EditGroupDialogData
    });
  }

  onOpenCreateMenuGroupDialog(): void {
    const groups = this.groupsMap;

    this.dialog.open(EditGroupModalComponent, {
      data: {
        group: null,
        groupsMap: groups,
        originalGroup: null,
        overrideGroupPosition: null,
        overrideGroupStructurePosition: null,
        organization: this.currentOrganization,
        organizationId: this.currentOrganization.id,
        menuSections: this.getMenuSectionsIncludeChildren(),
        menuSectionId: this.currentMenuSection.id,
        allowOverrides: this.allowOverrides,
        products: this.products,
      } as EditGroupDialogData
    });
  }

  getParentMenuSectionId(): string {
    // return menus section with parentMenuSectionId is equals to null (this menu section created automatically when sync menu).
    return this.menuSections.find(ms => ms.parentMenuSectionId === null).id;
  }

  createMenuSection($event: string | null): void {
    this.dialog.open(EditMenuModalComponent, {
      data: {
        copyFromMenuSectionId: $event,
        parentMenuSectionId: this.getParentMenuSectionId(),
        organizationId: this.currentOrganization.id,
      } as EditMenuModalComponentData
    });
  }

  editMenuSection($event: MenuSection | null): void {
    this.dialog.open(EditMenuModalComponent, {
      data: {
        menuSectionId: $event.id,
        name: $event.name,
        description: $event.description,
        parentMenuSectionId: $event.parentMenuSectionId,
        organizationId: this.currentOrganization.id,
      } as EditMenuModalComponentData
    });
  }

  async deleteMenuSection($event: MenuSection | null): Promise<void> {
    await this.deleteOrResoterMenuSection($event, true);
  }

  async restoreMenuSection($event: MenuSection | null): Promise<void> {
    await this.deleteOrResoterMenuSection($event, false);
  }

  async deleteOrResoterMenuSection(menuSection: MenuSection, isDeleted: boolean): Promise<void> {
    await this.menuService.updateMenuSection({
      id: menuSection.id,
      isDeleted: isDeleted,
      name: menuSection.name,
      description: menuSection.description,
      parentMenuSectionId: menuSection.parentMenuSectionId,
      organizationId: this.currentOrganization.id,
    });
  }

  async selectMenuSection(menuSectionId: string): Promise<void> {
    if(this.isMenuBackup(menuSectionId)){
      this.store.dispatch(setMenusPending(true));
      const request : GetMenuRequest = {
        menuName: menuSectionId,
        organizationId: this.currentOrganization.id,
      };
      const menuBackup = this.cachedBackups[menuSectionId] ?? await this.menuBackupService.getMenuSectionOrBackup(request);
      const menuState = this.menuConverter.convertMenu(menuBackup, new OverridePositionsMap([]), this.cachedBackups);
      if(!this.cachedBackups[menuSectionId]){
        var backupsMap = {...this.cachedBackups};
        backupsMap[menuSectionId] = menuBackup;
        menuState.menuBackups = {...backupsMap};
      }
      this.store.dispatch(MenuActions.updateMenuState({...menuState, getMenusPending: false, allowOverrides: false}));
    } else {
      await this.router.navigate(['./../', menuSectionId], {relativeTo: this.activatedRoute});

      // Код, который идет ниже, ранее был подписан на изменение url,
      // в котором была получено значение menuSectionId, но на данный момент непонятно, правильно ли это делать
      // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially
      this.store.dispatch(setMenusPending(true));
      this.currentMenuSection = this.menuSections.find(m => m.id === menuSectionId);
      const overridePositionsResponse = await this.dishesService.getOverrides(menuSectionId);
      const overridePositions = overridePositionsResponse.data;
      const overridePositionsMap = new OverridePositionsMap(overridePositions);

      const menuState = this.menuConverter.convertMenu(this.gecoPosMenu, overridePositionsMap, this.cachedBackups);
      // eslint-disable-next-line @ngrx/avoid-dispatching-multiple-actions-sequentially
      this.store.dispatch(MenuActions.updateMenuState({...menuState, overridePositionsMap: overridePositionsMap, getMenusPending: false, allowOverrides: true}));
      this.menuPipes = await this.getMenuPipes(this.currentOrganization.id, this.currentMenuSection);
    }
  }

  async dropGroup($event: CdkDragDrop<Group[]>) {
    if(this.isShowGroupHierarchy || !this.allowOverrides) {
      return;
    }

    if (this.searchQuery) {
      const message = this.translate.instant('MENU.WARNING.PROHIBITION_ON_ORDERING')
      this.snackbar.info(message);
      return;
    }
    if ($event.previousIndex !== $event.currentIndex) {
      moveItemInArray(this.groups, $event.previousIndex, $event.currentIndex);
      this.selectGroup(this.selectedGroup);
      const overridesGroupPositionsRequest = MenuOverrideService.saveGroupIdsInMenu(this.groups.map(g => g.externalId), this.currentMenuSection.menuId, this.currentMenuSection.id, this.overridePositionsMap);

      await this.menuService.saveOverridePositions(this.currentMenuSection.id,[overridesGroupPositionsRequest], [this.currentMenuSection.id]);
    }
  }

  getGroups(){
    if(this.isShowGroupHierarchy){
      return this.hierarchicalGroups;
    } else{
      return this.groups;
    }
  }

  selectGroup(group) {
    this.selectedGroup = group;
  }

  uploadMenu(): void {
    this.uploadingMenu = true;
    const successMessage = this.translate.instant('GECO_POS.UPLOAD_MENU_SUCCESS');
    const errorMessage = this.translate.instant('GECO_POS.UPLOAD_MENU_ERROR');
    this.apiService.uploadMenu(this.menuPipe.id)
      .subscribe({
        next: result => {
          if (result.error == null) {
            alert(successMessage);
            // по хорошему надо бы сделать ресет стора
            // но пока так
            location.reload();
          } else {
            console.log('uploadMenu:error', result.error);
            this.uploadingMenu = false;
            alert(errorMessage);
          }
        },
        error: error => {
          console.log('uploadMenu:error', error);
          this.uploadingMenu = false;
          alert(errorMessage);
        }
      });
  }

  onOpenEditDialogProduct(product: Product) {
    const originalProduct = this.originalProducts.get(product.externalId);
    this.dialog.open(EditProductModalComponent, {
      data: {
        product,
        originalProduct,
        menuSections: this.getMenuSectionsIncludeChildren(),
        menuSectionId: this.currentMenuSection.id,
        organization: this.currentOrganization,
        organizationId: this.currentOrganization.id,
        allowOverrides: this.allowOverrides,
        foreignBarcodes: this.collectForeignBarcodes(product.externalId),
      } as EditProductsModalComponentDataType
    });
  }

  // Штрихкоды фасовок всех блюд меню, кроме редактируемого (с учётом применённых оверрайдов):
  // iiko запрещает один штрихкод у двух товаров, карточка блокирует такое сохранение.
  private collectForeignBarcodes(editedProductExternalId: string): Map<string, string> {
    const foreignBarcodes = new Map<string, string>();
    this.products.forEach(product => {
      if (product.externalId === editedProductExternalId) {
        return;
      }
      for (const barcode of product.barcodes ?? []) {
        if (barcode.barcode && !foreignBarcodes.has(barcode.barcode)) {
          foreignBarcodes.set(barcode.barcode, product.name);
        }
      }
    });
    return foreignBarcodes;
  }

  async clearProductSorting() : Promise<void> {
    // если в папке лежат продукты из других папок, то нужно отсортировать id родных продуктов
    // и потом добавить в конец продукты из других папок.
    const originalProductsInGroupIds = this.originalGroupsMap.get(this.selectedGroup.externalId)
      .groupStructure.productsInGroup.map(p => p.externalProductId);

    const selectedProductInGroupIds = this.selectedGroup.groupStructure.productsInGroup.map(p => p.externalProductId);

    let nativeIds: string [] = [];
    let foreignIds: string [] = []

    for (let id of selectedProductInGroupIds) {
      if(originalProductsInGroupIds.some(opId => opId === id)){
        nativeIds.push(id);
      }
      else {
        foreignIds.push(id);
      }
    }

    let sortedNativeIds = [...originalProductsInGroupIds].filter(opId => nativeIds.some(id => id === opId));

    let newIds = [...sortedNativeIds, ...foreignIds];

    const overrideGroupStructure = MenuOverrideService.createOrUpdateProductIdsInGroupStructure(
      newIds,
      this.selectedGroup.externalId,
      this.currentMenuSection.id,
      this.overridePositionsMap);

    await this.menuService.saveOverridePositions(this.currentMenuSection.id,[overrideGroupStructure], [this.currentMenuSection.id]);
  }

  hasDisabledGroups(): boolean{
    return this.groups?.length !== this.originalGroupsMap?.size;
  }

  hasReOrderingGroups(): boolean{
    let nativeGroups = this.sortedOriginalGroups
      .filter(og => this.groups.some(g => g.externalId === og.externalId));
    let result = !menuHelper.equalGroups(this.groups, nativeGroups);
    return result;
  }

  changeShowGroups(value: boolean){
    this.isShowGroupHierarchy = value;
    if (value){
      this.hierarchicalGroups = this.getStructuredGroups(this.groups);
    } else {
      this.hierarchicalGroups = this.groups;
    }
  }

  getStructuredGroups(groups: DisplayedGroup[]) : DisplayedGroup[]{
    const structuredGroups:DisplayedGroup[] = [];

    const groupsByParent = groups.reduce((acc, group) => {
      const parentId = group.groupStructure.parentId || 'root';
      acc[parentId] = [...(acc[parentId] || []), group];
      return acc;
    }, {} as Dictionary<DisplayedGroup[]>);

    const dfs = (parentId: string) => {
      const children = groupsByParent[parentId] || [];

      for (const child of children) {
        structuredGroups.push(child)
        dfs(child.externalId)
      }
    };

    dfs('root');

    return structuredGroups;
  }

  async clearGroupSorting() : Promise<void> {
    // если скрытых групп нет, то нужно в override отправить пустой массив groups
    let nativeGroups = [...this.sortedOriginalGroups].filter(og => this.groups.some(g => g.externalId === og.externalId)); // оставшиеся группы в оригинальной сортировке

    let newGroupIds: string[] | null = null;

    if (this.groups.length !== this.sortedOriginalGroups.length){
      newGroupIds = nativeGroups.map(g => g.externalId);
    }

    const overrideGroupStructure = MenuOverrideService.saveGroupIdsInMenu(
      newGroupIds,
      this.currentMenuSection.menuId,
      this.currentMenuSection.id,
      this.overridePositionsMap);

    await this.menuService.saveOverridePositions(this.currentMenuSection.id,[overrideGroupStructure], [this.currentMenuSection.id]);
  }

  // У группы есть хотя бы одна дочерняя группа (т.е. она является родителем в иерархии).
  // Блюда, лежащие в такой группе напрямую, не показываются на киоске — киоск
  // показывает блюда только из листовых групп.
  groupHasChildren(group: DisplayedGroup | null | undefined): boolean {
    if (!group) {
      return false;
    }
    return this.parentIdsWithChildren.has(group.externalId);
  }

  private buildParentIdsWithChildren(groups: DisplayedGroup[]): Set<string> {
    const set = new Set<string>();
    for (const g of groups) {
      const parentId = g.groupStructure?.parentId;
      if (parentId) {
        set.add(parentId);
      }
    }
    return set;
  }

  isChildGroup(group: DisplayedGroup) : boolean {
    return this.isShowGroupHierarchy && !!group.groupStructure.parentId
  }

  // Последний ли это ребёнок у своего родителя — нужен SCSS-у, чтобы обрезать вертикальную
  // соединительную линию ровно на уровне горизонтального отвода (иначе у последнего ребёнка
  // линия продолжалась бы в пустоту).
  isLastChildInParent(group: DisplayedGroup) : boolean {
    if (!this.isChildGroup(group)) {
      return false;
    }
    const parentId = group.groupStructure.parentId;
    const siblings = this.hierarchicalGroups.filter(g => g.groupStructure.parentId === parentId);
    return siblings.length > 0 && siblings[siblings.length - 1].externalId === group.externalId;
  }

  // Возвращает количество родителей у группы
  getChildDeep(group: DisplayedGroup) : number {
    if(this.isChildGroup(group)){
      const parent = this.groupsMap.get(group.groupStructure.parentId) as DisplayedGroup;
      return this.getChildDeep(parent) + 1;
    }
    return 0;
  }

  // Возвращает стиль с вложенностью в зависимости от количества родителей
  getChildStyle(group: DisplayedGroup) : string {
    const deep = this.getChildDeep(group)
    if(deep){
      // Чтобы не было слишком большого отступа
      const maxDeep = 3;
      const deepForStyle = deep <= maxDeep ? deep : maxDeep;
      return `padding-left: ${deepForStyle * 8}%`;
    }
    return "";
  }

  async goToMenuEventsAsync(){
    const queryParams = {
      menuSectionId: this.currentMenuSection.id,
    } as MenuEventsQueryParams;
    await this.router.navigate(
      ['app', this.currentOrganization.id, 'menu-events'],
      {
        queryParams,
      });
  }

  showPipes(){
    this.dialog.open(
      MenuPipesModalComponent,
      {
        data: {
          roleMembership: this.roleMembership,
          menuPipes: this.menuPipes,
        } as MenuPipesModalComponentDataType,
        width: '1200px',
        maxHeight: '1200px',
      });
  }

  openLegacyToV2Migration(menuSection: MenuSection): void {
    this.openMigrationDialog('legacy-to-v2', menuSection);
  }

  openV2ToV3Migration(menuSection: MenuSection): void {
    this.openMigrationDialog('v2-to-v3', menuSection);
  }

  private openMigrationDialog(mode: 'legacy-to-v2' | 'v2-to-v3', menuSection: MenuSection): void {
    const menuSectionId = menuSection?.id;
    if (!menuSectionId) {
      this.snackbar.error('Menu section is not loaded yet');
      return;
    }

    const ref = this.dialog.open(MigrateRelatedProductsDialogComponent, {
      data: { mode, menuSectionId } as MigrateRelatedProductsDialogData,
      width: '1200px',
      maxHeight: '90vh',
      autoFocus: false,
    });
    ref.afterClosed().subscribe((result) => {
      if (result?.result?.applied > 0) {
        this.store.dispatch(setMenusPending());
      }
    });
  }

  loadRevisionForPipeGroups(pipes: PipeConfiguration[]){
    // ищу pipe gecopos_sales -> iiko
    const menuPipe = pipes.find(pipe => {
      return pipe.sourceConfiguration.externalSystemType === GECOPOSSALES_ID
        && pipe.sourceConfiguration.externalId === this.currentOrganization.id
        && pipe.isActive && !pipe.isDeleted;
    })

    // если есть, то хочу получить больше информации о нём
    if (menuPipe) {
      this.menuPipeId = menuPipe.id;
      this.ngxsStore.dispatch(new LoadRevisionForPipeGroups([menuPipe]))
    } else {
      this.menuPipeId = null;
    }

    this.getPipeDetails$.pipe(
      untilDestroyed(this)
    ).subscribe(value => {
      const menuPipe = value.find(value1 => value1.id === this.menuPipeId);
      if (this.menuPipeId && menuPipe) {
        this.menuPipe = menuPipe;
      } else {
        this.menuPipe = null;
      }
    })
  }

  async createMenuBackup(basedMenuSectionId: string){
    this.dialog.open(EditMenuBackupComponent,
      {
        data: {
          menuSectionName: this.menuSections.find(m => m.id == basedMenuSectionId)?.name,
        } as EditMenuBackupModalDataType
      })
      .afterClosed().subscribe(async res => {
      if (res) {
        const createMenuBackup : CreateMenuBackup = {
          name: res,
          basedMenuSectionId: basedMenuSectionId,
        }
        const response = await this.menuBackupService.createBackup(createMenuBackup);
        this.menuBackups = [...this.menuBackups, response];
      }
    });;

  }

  async deleteMenuBackup(id: string){
    await this.menuBackupService.deleteBackup(id);
    this.menuBackups = [...this.menuBackups.filter(backup => backup.id != id)];
  }

  async downloadMenuByName(backupId: string){
    var url = await this.menuBackupService.downloadMenu(backupId);
    AppHelper.openList(url);
  }

  private isMenuBackup(menuSectionId: string) {
    return !!this.menuBackups?.find(backup => backup.id === menuSectionId);
  }

  async getMenuPipes(organizationId: string, menuSection: MenuSection): Promise<PipeConfiguration[]> {
    const organizations = await firstValueFrom(this.ngxsStore.select(OrganizationsState.getSelectedOrganizationBranches));
    const organizationIds = organizations.map(o => o.id);
    const pipesResponse = await firstValueFrom(this.apiService.getPipes(organizationIds));
    const pipes = pipesResponse.result;
    let result: PipeConfiguration[] = [];
    let toGetPosPipes: PipeConfiguration[] = [];
    let fromGetPosPipes: PipeConfiguration[] = [];

    for (const pipe of pipes) {
      const fromGetPosPipe = pipe.targetConfiguration.externalSystemType === GECOPOS_ID;
      const toGetPosPipe = pipe.sourceConfiguration.externalSystemType === GECOPOSSALES_ID;
      if (!fromGetPosPipe && !toGetPosPipe){
        continue;
      }

      if (toGetPosPipe){
        toGetPosPipes.push(pipe);
        continue;
      }

      const configGetPos = JSON.parse(pipe.targetConfiguration.jsonRepresentation);
      const menuOrganizationId = configGetPos.MenuOrganizationId;

      if (menuOrganizationId != organizationId){
        continue;
      }

      const menuMappings = configGetPos.MenuMappings;

      for (const menuMapping of menuMappings) {
        if (menuMapping.systemName === menuSection.name){
          fromGetPosPipes.push(pipe);
        }
      }
    }

    result = [
      ...toGetPosPipes.filter(tp => fromGetPosPipes.some(fp => fp.organizationId === tp.organizationId)),
      ...fromGetPosPipes
    ]
    return result;
  }
}
